import { css } from "styled-components";
import isNumber from "lodash/isNumber";
import isString from "lodash/isString";
import Breakpoints from "../constants/breakpoints";

export type GetPaddingStyle = {
  padding?: string | number;
  px?: string | number;
  py?: string | number;
  pl?: string | number;
  pr?: string | number;
  pt?: string | number;
  pb?: string | number;
};

export type GetMarginStyle = {
  margin?: string | number;
  mx?: string | number;
  my?: string | number;
  ml?: string | number;
  mr?: string | number;
  mt?: string | number;
  mb?: string | number;
};

export type SpacingStyle = GetPaddingStyle & GetMarginStyle;

const spacingSizing = {
  [Breakpoints.desktop4]: {
    xs: "2px",
    s: "4px",
    m: "8px",
    l: "16px",
    xl: "24px",
    "2xl": "32px",
    "3xl": "48px",
    "4xl": "64px",
  },
  [Breakpoints.tablet]: {
    xs: "1.5px",
    s: "3px",
    m: "6px",
    l: "12px",
    xl: "18px",
    "2xl": "24px",
    "3xl": "32px",
    "4xl": "48px",
  },
  [Breakpoints.mobile3]: {
    xs: "1px",
    s: "2px",
    m: "4px",
    l: "8px",
    xl: "12px",
    "2xl": "16px",
    "3xl": "24px",
    "4xl": "32px",
  },
};

const getSizing = ({
  var1,
  var2,
  breakpoint = Breakpoints.desktop4,
}: {
  var1?: string | number;
  var2?: string | number;
  breakpoint?: number;
}) => {
  const isReservedString = ["xs", "s", "m", "l", "2xl"];
  let _var1 = var1;
  let _var2 = var2;

  if (isNumber(var1)) {
    _var1 = `${var1}px`;
  }

  if (isNumber(var2)) {
    _var2 = `${var2}px`;
  }

  if (isString(var1) && isReservedString.includes(var1)) {
    _var1 = spacingSizing[breakpoint]?.[var1];
  }

  if (isString(var2) && isReservedString.includes(var2)) {
    _var2 = spacingSizing[breakpoint]?.[var2];
  }

  return (_var1 || _var2) ?? "0px";
};

export const getPaddingStyle = ({ padding, px, py, pl, pr, pt, pb }: GetPaddingStyle) => {
  let pad = css``;

  if (!!padding)
    pad = pad.concat(css`
      padding: ${getSizing({ var1: padding })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        padding: ${getSizing({ var1: padding, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        padding: ${getSizing({ var1: padding, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!pl || !!px)
    pad = pad.concat(css`
      padding-left: ${getSizing({ var1: pl, var2: px })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        padding-left: ${getSizing({ var1: pl, var2: px, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        padding-left: ${getSizing({ var1: pl, var2: px, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!pr || !!px)
    pad = pad.concat(css`
      padding-right: ${getSizing({ var1: pr, var2: px })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        padding-right: ${getSizing({ var1: pr, var2: px, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        padding-right: ${getSizing({ var1: px, var2: px, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!pt || !!py)
    pad = pad.concat(css`
      padding-top: ${getSizing({ var1: pt, var2: py })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        padding-top: ${getSizing({ var1: pt, var2: py, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        padding-top: ${getSizing({ var1: pt, var2: py, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!pb || !!py)
    pad = pad.concat(css`
      padding-bottom: ${getSizing({ var1: pb, var2: py })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        padding-bottom: ${getSizing({ var1: pb, var2: py, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        padding-bottom: ${getSizing({ var1: pb, var2: py, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  return pad;
};

export const getMarginStyle = ({ margin, mx, my, ml, mr, mt, mb }: GetMarginStyle) => {
  let marg = css``;

  if (!!margin)
    marg = marg.concat(css`
      margin: ${getSizing({ var1: margin })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        margin: ${getSizing({ var1: margin, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        margin: ${getSizing({ var1: margin, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!ml || !!mx)
    marg = marg.concat(css`
      margin-left: ${getSizing({ var1: ml, var2: mx })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        margin-left: ${getSizing({ var1: ml, var2: mx, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        margin-left: ${getSizing({ var1: ml, var2: mx, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!mr || !!mx)
    marg = marg.concat(css`
      margin-right: ${getSizing({ var1: mr, var2: mx })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        margin-right: ${getSizing({ var1: mr, var2: mx, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        margin-right: ${getSizing({ var1: mr, var2: mx, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!mt || !!my)
    marg = marg.concat(css`
      margin-top: ${getSizing({ var1: mt, var2: my })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        margin-top: ${getSizing({ var1: mt, var2: my, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        margin-top: ${getSizing({ var1: mt, var2: my, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  if (!!mb || !!my)
    marg = marg.concat(css`
      margin-bottom: ${getSizing({ var1: mb, var2: my })};

      @media screen and (max-width: ${Breakpoints.tablet}px) {
        margin-bottom: ${getSizing({ var1: mb, var2: my, breakpoint: Breakpoints.tablet })};
      }

      @media screen and (max-width: ${Breakpoints.mobile3}px) {
        margin-bottom: ${getSizing({ var1: mb, var2: my, breakpoint: Breakpoints.mobile3 })};
      }
    `);

  return marg;
};
